import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/apiClient';
import { listsKeys, SearchListsParams } from '@/api/keys';
import {
  RestaurantListDetails,
  GetListsResponse,
  SearchListsResponse,
  GetUserListsResponse,
  GetTagsResponse,
  GetListsByTagResponse,
  GetActivityFeedResponse,
  PaginatedListsResponse,
  GetListsByTagPaginatedResponse,
  GetFeaturedListsResponse,
} from '@/types/lists/restaurantList';
import { RestaurantListsResponse } from '@/types/server/restaurantApiResponses';

// Re-export types for backward compatibility
export interface TrendingList {
  urlKey: string;
  name: string;
  description: string;
  creatorName: string;
  restaurantCount: number;
  followerCount: number;
  coverArt?: string;
}

export interface TrendingListsResponse {
  trending: TrendingList[];
}

/**
 * Hook to get all user's lists
 */
export function useGetLists(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: listsKeys.lists(),
    queryFn: () => apiFetch<GetListsResponse>('/lists/'),
    staleTime: 1000 * 60 * 2, // 2 minutes — avoids refetch on every tab switch
    enabled: options?.enabled,
  });
}

/**
 * Hook to get a specific user's lists
 * - Returns only public lists for other users
 * - Returns all lists for own profile
 */
export function useGetUserLists(userId: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: listsKeys.byUser(userId),
    queryFn: () => apiFetch<GetUserListsResponse>(`/users/${userId}/lists/`),
    enabled: (options?.enabled ?? true) && !!userId,
  });
}

/**
 * Hook to search user's lists
 */
export function useSearchLists(
  params: SearchListsParams,
  options?: { enabled?: boolean }
) {
  const hasValidSearch = !!(params.searchQuery && params.searchQuery.length >= 2);

  return useQuery({
    queryKey: listsKeys.search(params),
    queryFn: () =>
      apiFetch<SearchListsResponse>('/lists/search/', {
        params: {
          ...(params.searchQuery && { searchQuery: params.searchQuery }),
          limit: params.limit ?? 10,
        },
      }),
    enabled: (options?.enabled ?? true) && hasValidSearch,
    gcTime: 0, // Don't cache search results (keepUnusedDataFor: 0 equivalent)
  });
}

/**
 * Hook to get list details by URL key
 */
export function useGetListDetails(urlKey: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: listsKeys.detail(urlKey),
    queryFn: () => apiFetch<RestaurantListDetails>(`/lists/${urlKey}/details/`),
    enabled: options?.enabled ?? !!urlKey,
  });
}

/**
 * Hook to get lists that contain a specific restaurant
 */
export function useGetRestaurantLists(
  restaurantId: number,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: listsKeys.restaurantLists(restaurantId),
    queryFn: () =>
      apiFetch<RestaurantListsResponse>(`/restaurants/${restaurantId}/lists/`),
    enabled: options?.enabled ?? !!restaurantId,
  });
}

/**
 * Hook to get trending public lists
 */
export function useGetTrendingLists(limit?: number) {
  return useQuery({
    queryKey: listsKeys.trending(limit),
    queryFn: () =>
      apiFetch<TrendingListsResponse>('/lists/trending/', {
        params: { limit: limit ?? 4 },
      }),
  });
}

/**
 * Hook to get all available list tags
 */
export function useGetTags(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: listsKeys.tags(),
    queryFn: () => apiFetch<GetTagsResponse>('/lists/tags/'),
    enabled: options?.enabled,
    staleTime: 1000 * 60 * 5, // Tags don't change often, cache for 5 minutes
  });
}

/**
 * Hook to get lists filtered by tag
 */
export function useGetListsByTag(tagSlug: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: listsKeys.byTag(tagSlug),
    queryFn: () => apiFetch<GetListsByTagResponse>(`/lists/by-tag/${tagSlug}/`),
    enabled: (options?.enabled ?? true) && !!tagSlug,
  });
}

/**
 * Hook to get activity feed for followed lists
 */
export function useGetActivityFeed(options?: { enabled?: boolean; refetchInterval?: number }) {
  return useQuery({
    queryKey: listsKeys.activityFeed(),
    queryFn: () => apiFetch<GetActivityFeedResponse>('/lists/activity-feed/'),
    enabled: options?.enabled,
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * Infinite-scroll hook for the community (public) lists tab. Hits /lists/community/.
 */
export function useGetCommunityLists(options?: { enabled?: boolean }) {
  return useInfiniteQuery({
    queryKey: listsKeys.communityLists(),
    queryFn: ({ pageParam }) =>
      apiFetch<PaginatedListsResponse>('/lists/community/', {
        params: { cursor: pageParam ?? undefined, pageSize: 20 },
      }),
    initialPageParam: null as number | null,
    getNextPageParam: (lastPage) =>
      lastPage.hasNext ? lastPage.nextCursor : undefined,
    enabled: options?.enabled,
  });
}

/**
 * Staff-featured public lists for the Lists page's Featured tab. Hits
 * /lists/featured/ — the same editorial selection the Digest's Featured
 * Lists module uses. Plain query, not infinite: the endpoint is unpaginated.
 */
export function useGetFeaturedLists(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: listsKeys.featuredLists(),
    queryFn: () => apiFetch<GetFeaturedListsResponse>('/lists/featured/'),
    staleTime: 1000 * 60 * 5, // Editorially curated — changes rarely
    enabled: options?.enabled,
  });
}

/**
 * Infinite-scroll hook for tag-filtered lists.
 */
export function useGetListsByTagInfinite(
  tagSlug: string,
  options?: { enabled?: boolean }
) {
  return useInfiniteQuery({
    queryKey: listsKeys.byTagInfinite(tagSlug),
    queryFn: ({ pageParam }) =>
      apiFetch<GetListsByTagPaginatedResponse>(`/lists/by-tag/${tagSlug}/`, {
        params: { cursor: pageParam ?? undefined, pageSize: 20 },
      }),
    initialPageParam: null as number | null,
    getNextPageParam: (lastPage) =>
      lastPage.hasNext ? lastPage.nextCursor : undefined,
    enabled: (options?.enabled ?? true) && !!tagSlug,
  });
}
